import React from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2 } from 'lucide-react';
import CdmoBulkUpload from '../components/cdmoImport/CdmoBulkUpload';
import { CDMO_IMPORT_MESSAGES } from '../constants/messages';
import { useAuth } from '../context/AuthContext';

const CDMO_DASHBOARD_PATH = '/cdmo-dashboard';

/**
 * Admin-only page for the CDMO bulk upload (PHAR-485): CSV → validated preview → unclaimed,
 * admin-created profiles.
 *
 * `ProtectedRoute` already sends signed-out and unverified users away. This page then waits for the
 * admin lookup to resolve before deciding — the same gate as `CampaignPage` and `AdminAccessPage` —
 * so an admin never sees a flash of "unauthorised" while their session restores, and anyone else is
 * redirected. Writes are also limited server-side: the audit table's RLS admits only admins.
 */
const AdminCdmoImportPage: React.FC = () => {
  const { isAdmin, adminCheckLoading, adminStatusResolved, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const awaitingAdminDecision = authLoading || (Boolean(user?.email) && !adminStatusResolved);

  if (awaitingAdminDecision || adminCheckLoading || !user) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50 max-w-md w-full"
        >
          <Loader2 className="w-12 h-12 text-electric-blue mx-auto mb-4 animate-spin" />
          <p className="text-slate-gray">Checking access…</p>
        </motion.div>
      </div>
    );
  }

  // Rendered only for a confirmed admin; anything else — including a lookup that never resolved —
  // is redirected.
  if (!isAdmin) {
    return (
      <Navigate
        to="/unauthorized?reason=cdmo-import"
        replace
        state={{ message: CDMO_IMPORT_MESSAGES.NOT_AUTHORIZED }}
      />
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-light-gray to-sky-blue/20">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Link
          to={CDMO_DASHBOARD_PATH}
          className="inline-flex items-center gap-1.5 mb-4 text-sm font-medium text-slate-gray hover:text-selected-blue transition-colors rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-electric-blue/40"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          {CDMO_IMPORT_MESSAGES.BACK_TO_DASHBOARD}
        </Link>
        {/* Opacity only: a transform here would become the containing block of the fixed overlays. */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <CdmoBulkUpload
            onExit={() => navigate(CDMO_DASHBOARD_PATH)}
            adminEmail={user.email ?? ''}
            adminUserId={user.id}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default AdminCdmoImportPage;
